// An app's page, `apps/<id>` (01-the-console.md § the address; § the moves,
// page by page: Apps; decision 0069): what it acts as, its state, who
// connected it and when, its last use (hq decision 0072), when a personal app
// was last confirmed (04-apps.md § re-attestation and suspension), its key's age, and its
// id, quiet, last. *New key* is the page's primary action and hands the new
// key over in the page's place, shown once; *Restore* and *Disconnect* are in
// the page's menu, each asked first. A personal app suspended for want of its
// person's sign-in says why and what lifts it (04-apps.md § re-attestation
// and suspension; decision 0033). The page reads the apps the role is
// answered by the list verb, so a member reaches their own apps and an admin
// any.

import { Fragment, type ReactNode } from "react";
import { Link, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { usePage } from "../../platform/session.tsx";
import {
  Empty,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  Pill,
  since,
  useLoad,
  when,
} from "../../ui/components.tsx";
import { useFormState } from "../../ui/form.tsx";
import { Ident } from "../../ui/ident.tsx";
import { RowMenu, type MenuEntry } from "../../ui/table.tsx";
import { Subjects, type App, type AppsListReply } from "../../wire/auth.ts";
import { SERVICE } from "../../wire/identity.ts";
import { useSurface } from "../surface.tsx";
import { AskedButton } from "./asked.tsx";
import { useAppActs, type AppActs } from "./acts.tsx";
import { KeyForm } from "./forms.tsx";
import { appFacts, appState, isSuspended, suspendedWhy } from "./state.ts";

export function AppPage() {
  const { id = "" } = useParams();
  const s = useSurface();
  const { platform } = usePage();
  const [apps, reload] = useLoad("apps", () =>
    s.bus.call<AppsListReply>(SERVICE, Subjects.appsList(), undefined),
  );
  const form = useFormState<"key">();
  const acts = useAppActs(s, reload, form.open, () => {
    form.show("key");
  });
  const gateway = platform.gateway_url || undefined;
  const app = apps.state === "ready" ? apps.value.apps.find((a) => a.id === id) : undefined;
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "apps")}>Apps</Link>
      <span>/</span>
      <span>{app?.name ?? id}</span>
    </>
  );

  if (acts.key !== undefined) {
    return (
      <KeyForm
        reply={acts.key.reply}
        fresh={false}
        gateway={gateway}
        back={acts.key.reply.app.name}
        onClose={form.close}
      />
    );
  }
  if (apps.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the app" />
      </>
    );
  }
  if (apps.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title="An app" />
        <NotAllowed err={apps.err} what="Apps" />
      </>
    );
  }
  if (app === undefined) {
    return (
      <>
        <PageHead crumb={crumb} title="No such app" />
        <Empty>No app of yours, or that you may see, has this id.</Empty>
      </>
    );
  }
  return <One app={app} crumb={crumb} entries={acts.menuFor(app)} err={acts.err} acts={acts} />;
}

function One({
  app,
  crumb,
  entries,
  err,
  acts,
}: {
  app: App;
  crumb: ReactNode;
  entries: MenuEntry[];
  err: unknown;
  acts: AppActs;
}) {
  const s = useSurface();
  const st = appState(app);
  const newKey = entries.find((e) => e.label === "New key");
  const rest = entries.filter((e) => e.label !== "New key");
  const why = isSuspended(app)
    ? suspendedWhy(acts.byName(app), app.registrant.subject === s.me, s.name, acts.restores)
    : undefined;
  return (
    <>
      <PageHead
        crumb={crumb}
        title={app.name}
        line={`Acts as ${acts.actsAs(app)}.`}
        action={
          <>
            {newKey !== undefined && <AskedButton entry={newKey} />}
            <RowMenu label={`More for ${app.name}`} entries={rest} />
          </>
        }
      />
      {err !== undefined && <NotAllowed err={err} />}
      {why !== undefined && (
        <Notice kind="bad" title={`Suspended ${since(app.suspended ?? "") || when(app.suspended ?? "")}`}>
          {why}
        </Notice>
      )}
      <section className="section">
        <div className="card">
          <dl className="kv">
            <dt>Status</dt>
            <dd>
              <Pill tone={st.tone}>{st.word}</Pill>
            </dd>
            {appFacts(app, { listsAny: s.offered("apps:list:any"), ago: since, at: when }).map((f) => (
              <Fragment key={f.label}>
                <dt>{f.label}</dt>
                <dd>
                  {f.value}
                  {f.note !== undefined && f.note !== "" && <span className="muted"> · {f.note}</span>}
                </dd>
              </Fragment>
            ))}
            <dt>Id</dt>
            <dd>
              <Ident value={app.id} label="the app's id" />
            </dd>
          </dl>
        </div>
      </section>
    </>
  );
}
